import React, { useContext, useMemo, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';

import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import Row from 'components/Common/Row';
import usePopup from 'hooks/usePopup';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Button from 'kingpin/atoms/Button';

import EntityAppForm from './EntityAppForm';
import EntityAppListItem from './EntityAppListItem';
import FormInput from '../../../../kingpin/forms/FormInput';
import Dropdown from '../../../../components/Inputs/Dropdown';
import STORE from '../../../../store';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import Alert from '../../../../kingpin/Alert';
import Typography from '../../../../kingpin/atoms/Typography';

const CreateAppButton = ({ entity }: { entity: EntityDetails.Entity }) => {
  const { isOpen, open, close } = usePopup();

  return (
    <>
      <Button
        label={'Create new app'}
        onClick={open}
        size={'Medium'}
        type={'Primary'}
      />
      <ModalTransition>
        {isOpen && <EntityAppForm entity={entity} close={close} />}
      </ModalTransition>
    </>
  );
};

const ManageEntityAppsPopup = ({
  entity,
  close,
}: {
  entity: EntityDetails.Entity;
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const apps = useMemo(() => {
    return entityDetailsApps.filter((e) => e.entityId === entity.id);
  }, [entity.id, entityDetailsApps]);

  const defaultAppOptions = useMemo((): DropdownOption[] => {
    return apps.map((app) => {
      return {
        label: app.title,
        subLabel: `/${app.urlSlug}`,
        isSelected: entity.defaultAppId === app.id,
        onSelected: () => {
          if (isLoading) {
            return;
          }
          setIsLoading(true);
          const newEntity: EntityDetails.Entity = {
            ...entity,
            defaultAppId: app.id,
          };
          STORE.getEntityDefinitionsRef({
            accountId: selectedAccountId,
          })
            .doc(newEntity.id)
            .set(newEntity)
            .then(() => {
              setIsLoading(false);
            });
        },
        isDisabled: isLoading,
      };
    });
  }, [apps, entity, isLoading, selectedAccountId]);

  return (
    <>
      <Form>
        <FormHeader title={`Manage "${entity.name}" apps`} onClose={close} />
        <FormContent>
          <FormInput label={'Default App'}>
            <Dropdown
              options={defaultAppOptions}
              testId="default-app-dropdown"
            />
            <div style={{ marginTop: 8 }}>
              <Alert
                type={'Info'}
                body={
                  'This controls which app is opened when this entity is presented as a drilldown in grids or board slideouts'
                }
              />
            </div>
          </FormInput>
          <FormInput label={'Apps'}>
            <Row
              style={{
                borderBottom: `1px solid var(--color-border-secondary)`,
              }}
            >
              <div style={{ width: 200 }}>
                <Typography.Body type={'Body 12'}>Name</Typography.Body>
              </div>
              <div style={{ width: 200 }}>
                <Typography.Body type={'Body 12'}>URL</Typography.Body>
              </div>
            </Row>
            {apps.map((app) => (
              <EntityAppListItem key={app.id} entity={entity} entityApp={app} />
            ))}
          </FormInput>
        </FormContent>
        <Row spaceBetween centerAlign>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Close'}
            onClick={close}
          />
          <CreateAppButton entity={entity} />
        </Row>
      </Form>
    </>
  );
};

export default ManageEntityAppsPopup;
